import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ZONES_IMAGE = "https://customer-assets.emergentagent.com/job_38ca16b8-b881-4255-a6f4-b41a6f37c1b3/artifacts/mps6cefb_Permaculture%20zones.png";

const zones = [
  {
    id: 0,
    name: "Zone 0",
    subtitle: "The Home",
    color: "#8B4513",
    position: { left: '50%', top: '60%' },
    items: ["Living spaces", "Indoor climate control", "Kitchen & food prep", "Storage areas", "Main water & energy hub"],
    description: "The center of all activity - your home. This is where you live, eat, and manage the entire system from."
  },
  {
    id: 1,
    name: "Zone 1",
    subtitle: "Kitchen Garden",
    color: "#228B22",
    position: { left: '60%', top: '52%' },
    items: ["Herbs & salad greens", "Frequently harvested crops", "Seedling nursery", "Compost bins", "Small greenhouse"],
    description: "Visited multiple times daily. Intensive gardening with herbs, salads, and high-maintenance crops that need constant attention."
  },
  {
    id: 2,
    name: "Zone 2",
    subtitle: "Food Forest & Orchards",
    color: "#32CD32",
    position: { left: '37%', top: '47%' },
    items: ["Fruit trees", "Berry bushes", "Perennial vegetables", "Small livestock", "Larger ponds"],
    description: "Visited often for orchard maintenance, food forest tending, and small animal care. Requires regular but less intensive attention."
  },
  {
    id: 3,
    name: "Zone 3",
    subtitle: "Farm Zone",
    color: "#9ACD32",
    position: { left: '68%', top: '37%' },
    items: ["Main crops", "Grain fields", "Fodder crops", "Farm ponds", "Grazing rotation"],
    description: "Commercial crops and field cultivation. Larger scale production requiring less frequent but systematic management."
  },
  {
    id: 4,
    name: "Zone 4",
    subtitle: "Semi-Managed Woodland",
    color: "#6B8E23",
    position: { left: '25%', top: '30%' },
    items: ["Timber harvest", "Wild foods", "Wildlife corridors", "Firewood collection", "Managed grazing"],
    description: "Semi-wild areas for timber, wild harvest, and managed woodland ecosystems. Visited occasionally for specific harvests."
  },
  {
    id: 5,
    name: "Zone 5",
    subtitle: "Wilderness",
    color: "#2E8B57",
    position: { left: '82%', top: '20%' },
    items: ["Native forest", "Wildlife sanctuary", "Observation & learning", "Seed source", "Water catchment"],
    description: "Unmanaged natural ecosystems. Observe and learn from nature's wisdom. Minimal human intervention."
  }
];

export const PermacultureZonesDial = () => {
  const [activeZone, setActiveZone] = useState(null);

  const currentZone = activeZone !== null ? zones.find(z => z.id === activeZone) : null;

  const handleZoneClick = (zoneId) => {
    setActiveZone(activeZone === zoneId ? null : zoneId);
  };

  return (
    <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12" data-testid="permaculture-zones-dial">
      {/* Zones Image with clickable zone indicators */}
      <div className="w-full max-w-lg lg:max-w-xl">
        <div className="relative">
          <motion.img 
            src={ZONES_IMAGE}
            alt="Permaculture Zones - concentric circles showing Zone 0 (home) at center through Zone 5 (wilderness) at the edge"
            className="w-full h-auto rounded-2xl shadow-lg"
            loading="lazy"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          />
          
          {/* Zone number indicators positioned on the image */}
          {zones.map((zone) => (
            <button
              key={zone.id}
              onClick={() => handleZoneClick(zone.id)}
              data-testid={`zone-indicator-${zone.id}`}
              className="absolute flex items-center justify-center rounded-full border-2 border-white shadow-lg cursor-pointer transition-all duration-300 hover:scale-125 focus:outline-none focus:ring-2 focus:ring-white/50"
              style={{
                left: zone.position.left,
                top: zone.position.top,
                transform: 'translate(-50%, -50%)',
                width: activeZone === zone.id ? '2.25rem' : '1.75rem',
                height: activeZone === zone.id ? '2.25rem' : '1.75rem',
                backgroundColor: zone.color,
                zIndex: activeZone === zone.id ? 20 : 10,
                boxShadow: activeZone === zone.id 
                  ? `0 0 0 4px ${zone.color}40, 0 4px 12px rgba(0,0,0,0.3)` 
                  : '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              <span className="text-white font-bold text-xs leading-none">{zone.id}</span>
            </button>
          ))}
        </div>
        
        {/* Zone Selector Buttons below image */}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {zones.map((zone) => (
            <button
              key={zone.id}
              onClick={() => handleZoneClick(zone.id)}
              data-testid={`zone-btn-${zone.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeZone === zone.id
                  ? 'text-white shadow-md scale-105'
                  : 'bg-white/80 text-foreground/70 hover:bg-white hover:shadow-sm border border-border/30'
              }`}
              style={activeZone === zone.id ? { backgroundColor: zone.color } : {}}
            >
              {zone.name}: {zone.subtitle}
            </button>
          ))}
        </div>
      </div>

      {/* Zone Info Panel */}
      <div className="flex-1 max-w-md w-full">
        <AnimatePresence mode="wait">
          {currentZone ? (
            <motion.div
              key={currentZone.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-border/30"
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4"
                style={{ backgroundColor: currentZone.color }}
              >
                {currentZone.id}
              </div>
              <h3 className="font-serif text-2xl text-forest mb-1">{currentZone.name}</h3>
              <p className="text-earth font-medium mb-4">{currentZone.subtitle}</p>
              <p className="text-foreground/70 text-sm leading-relaxed mb-6">{currentZone.description}</p>
              
              <h4 className="text-sm font-semibold text-forest mb-3">What belongs here:</h4>
              <ul className="space-y-2">
                {currentZone.items.map((item, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-3 text-sm text-foreground/70"
                  >
                    <span 
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: currentZone.color }}
                    />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-sand/50 rounded-2xl p-6 md:p-8 text-center border border-border/20"
            >
              <div className="w-14 h-14 rounded-xl bg-forest/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-forest text-2xl">?</span>
              </div>
              <h3 className="font-serif text-xl text-forest mb-2">Explore the Zones</h3>
              <p className="text-foreground/60 text-sm mb-4">
                Click on a numbered marker on the image or select a zone below to learn about each permaculture zone.
              </p>
              <p className="text-foreground/50 text-xs">
                Zone 0 (red house at center) to Zone 5 (outer wilderness)
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
